import type { DensitySnapshot, LockedCard } from "@screener/protocol";
import type { DensityCard } from "./cards";
import type { DensityFilters, DensitySort } from "./derive";

/**
 * Закрытые карточки тарифа (протокол 12) в сетке карточек. Чистый модуль без
 * React — тем же приёмом, что `cards.ts`. Закрытая карточка не плотность:
 * у неё нет монеты, цены и идентификатора, поэтому ни в таблицу, ни в вид по
 * монете, ни в счётчики она не попадает.
 */

/** Префикс ключа закрытой карточки в сетке: ключи инструментов («биржа|рынок|…») с ним не совпадают. */
export const LOCKED_CARD_KEY_PREFIX = "locked:";

/** Закрытая карточка как элемент сетки; строк у неё нет, раскладка видит пустую карточку. */
export interface LockedGridCard {
  key: string;
  locked: LockedCard;
  rows: readonly DensitySnapshot[];
}

export type GridCard = DensityCard | LockedGridCard;

export function isLockedGridCard(card: GridCard): card is LockedGridCard {
  return "locked" in card;
}

function toLockedGridCard(card: LockedCard): LockedGridCard {
  return { key: `${LOCKED_CARD_KEY_PREFIX}${card.key}`, locked: card, rows: [] };
}

/**
 * Закрытые карточки, которые подчиняются фильтрам экрана. У закрытой карточки
 * есть только биржа, сторона и диапазон объёма, поэтому:
 * - фильтр бирж пользователя убирает карточку, если ни один включённый рынок
 *   не принадлежит её бирже;
 * - минимальный размер убирает карточку, чей диапазон целиком не выше порога
 *   (верхняя граница не больше порога); без верхней границы диапазон порогом
 *   не отсекается;
 * - «только избранное» — фильтр по монете, а монеты у закрытой карточки нет:
 *   закрытых карточек нет вовсе.
 * Сравниваются числа, пришедшие от демона; своего расчёта объёма нет.
 */
export function filterLockedCards(
  locked: Iterable<LockedCard>,
  filters: DensityFilters,
): LockedCard[] {
  if (filters.onlyFavorites) {
    return [];
  }
  const exchanges = filters.exchanges;
  const minUsd = filters.minUsd;
  return [...locked].filter((card) => {
    if (exchanges !== null && !exchanges.some((key) => key.startsWith(`${card.exchange}:`))) {
      return false;
    }
    if (minUsd !== null && card.bucket_max_usd !== null && Number(card.bucket_max_usd) <= minUsd) {
      return false;
    }
    return true;
  });
}

/** Порядок сетки на момент заморозки: ключи карточек, включая закрытые. */
export function gridLayout(grid: readonly GridCard[]): string[] {
  return grid.map((card) => card.key);
}

/**
 * Замороженная сетка (§77): открытые карточки замороженного вида и снимок
 * закрытых на момент нажатия расставляются в порядке снимка. Карточка,
 * которой в снимке порядка нет, не появляется.
 */
export function composeFrozenGrid(
  cards: readonly DensityCard[],
  locked: readonly LockedCard[],
  layout: readonly string[],
): GridCard[] {
  const byKey = new Map<string, GridCard>();
  for (const card of cards) {
    byKey.set(card.key, card);
  }
  for (const card of locked) {
    const item = toLockedGridCard(card);
    byKey.set(item.key, item);
  }
  return layout.flatMap((key) => byKey.get(key) ?? []);
}

/** Нижняя граница диапазона для упорядочения. `Number` — только для сравнения, нигде не хранится. */
function volumeOf(locked: LockedCard): number {
  const amount = Number(locked.bucket_min_usd);
  return Number.isFinite(amount) ? amount : 0;
}

/**
 * Закрытые карточки среди открытых. Закреплённые карточки остаются выше всех.
 *
 * Сортировка по объёму: объём закрытой карточки — нижняя граница диапазона,
 * объём открытой — её крупнейшая плотность; при равенстве открытая выше.
 * Другие ключи сортировки (дистанция, возраст) у закрытой карточки не
 * определены — демон монету и цену не раскрывает, — и закрытые идут после
 * открытых, между собой по убыванию нижней границы. Расчёта объёма нет:
 * сравниваются числа, пришедшие от демона.
 */
export function mergeLockedCards(
  cards: readonly DensityCard[],
  locked: Iterable<LockedCard>,
  sort: DensitySort,
): readonly GridCard[] {
  const lockedItems: LockedGridCard[] = [...locked]
    .sort((left, right) => {
      const diff = volumeOf(right) - volumeOf(left);
      if (diff !== 0) {
        return diff;
      }
      return left.key < right.key ? -1 : left.key > right.key ? 1 : 0;
    })
    .map(toLockedGridCard);
  if (lockedItems.length === 0) {
    return cards;
  }

  const pinned = cards.filter((card) => card.pinned);
  const rest = cards.filter((card) => !card.pinned);
  if (sort.key !== "usd") {
    return [...pinned, ...rest, ...lockedItems];
  }

  const sign = sort.direction === "asc" ? 1 : -1;
  const entries: Array<{ card: GridCard; usd: number; locked: boolean; order: number }> = [
    ...rest.map((card, order) => ({
      card,
      usd: card.maxUsd,
      locked: false,
      order,
    })),
    ...lockedItems.map((card, order) => ({
      card,
      usd: volumeOf(card.locked),
      locked: true,
      order,
    })),
  ];
  entries.sort((left, right) => {
    const diff = sign * (left.usd - right.usd);
    if (diff !== 0) {
      return diff;
    }
    if (left.locked !== right.locked) {
      return left.locked ? 1 : -1;
    }
    return left.order - right.order;
  });
  return [...pinned, ...entries.map((entry) => entry.card)];
}

/** Одна граница диапазона: число и единица («5», «млн»). */
function boundary(amountUsd: number): { value: string; unit: string } {
  const [divisor, unit] =
    amountUsd >= 1_000_000 ? [1_000_000, "млн"] : amountUsd >= 1_000 ? [1_000, "тыс."] : [1, ""];
  const scaled = amountUsd / divisor;
  return { value: String(Number(scaled.toFixed(1))), unit };
}

/**
 * Диапазон объёма закрытой карточки: «$1–5 млн», без верхней границы —
 * «> $5 млн». Единицы разные — пишутся у каждой границы. Нечисловая граница
 * выводится как пришла: подпись не должна терять то, что сказал демон.
 */
export function formatLockedRange(minUsd: string, maxUsd: string | null): string {
  const min = Number(minUsd);
  if (!Number.isFinite(min)) {
    return maxUsd === null ? `> $${minUsd}` : `$${minUsd}–${maxUsd}`;
  }
  const low = boundary(min);
  if (maxUsd === null) {
    return `> $${low.value}${low.unit === "" ? "" : ` ${low.unit}`}`;
  }
  const max = Number(maxUsd);
  if (!Number.isFinite(max)) {
    return `$${low.value}${low.unit === "" ? "" : ` ${low.unit}`}–${maxUsd}`;
  }
  const high = boundary(max);
  if (low.unit === high.unit) {
    return `$${low.value}–${high.value}${high.unit === "" ? "" : ` ${high.unit}`}`;
  }
  const lowText = `$${low.value}${low.unit === "" ? "" : ` ${low.unit}`}`;
  const highText = `$${high.value}${high.unit === "" ? "" : ` ${high.unit}`}`;
  return `${lowText}–${highText}`;
}
